"use client";

import { useState, useEffect } from "react";
import { auth, db } from "../utils/data/firebase/firebase";
import { User, createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { AuthUser, UserRole } from "@/components/Props/adminProps";

export default function useFirebaseAuth() {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const authStateChanged = async (authState: User | null) => {
    if (!authState) {
      setAuthUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    let role: UserRole | null = null;
    let groupIds: string[] = [];

    try {
      const userDoc = await getDoc(doc(db, "users", authState.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        role = (data?.role as UserRole) ?? null;
        groupIds = data?.groupIds ?? [];
      }
    } catch (err) {
      console.error("Error fetching user doc:", err);
    }

    setAuthUser({
      uid: authState.uid,
      email: authState.email,
      role,
      groupIds,
    });
    setLoading(false);
  };

  const clear = () => {
    setAuthUser(null);
    setLoading(true);
  };

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const createUser = async (email: string, password: string) =>
    await createUserWithEmailAndPassword(auth, email, password);

  const signOut = async () => {
    await auth.signOut();
    clear();
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(authStateChanged);
    return () => unsubscribe();
  }, []);

  return {
    authUser,
    loading,
    signIn,
    createUser,
    signOut,
  };
}
